import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card, Modal, Row, Col, Form } from "react-bootstrap";
import { gql } from "apollo-boost";
import * as Cookies from "es-cookie";
import moment from "moment";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import DateBlock from "./DateBlock";
import CardLessDataTableWithSearch from "../datatables/CardLessDataTableWithSearch";
import { getAuthorizedSubjectIds } from "../../utils/authHelper";
import ExamAnalyticsDashboard from "./examAnalytics/ExamAnalyticsDashboard";

const GET_DASHBOARD_EXAMS = gql`
  query getDashBoardExams($institution_id: Int!, $branch_id: String) {
    getDashBoardExams(institution_id: $institution_id, branch_id: $branch_id) {
      full_exams {
        id
        exam_name
        exam_type
        class_id
        start_time
        section {
          id
          section_name
          branch_id
          exam_id
        }
      }
      quiz_exams {
        id
        exam_name
        exam_type
        class_id
        start_time
      }
    }
  }
`;

// Best-effort field shape — StudentTableInputInfo's real schema hasn't been confirmed
// against the backend yet (same "guess, then correct against the live response"
// approach used for the earlier dashboard queries in this file).
const GET_STUDENT_TABLE_DATA = gql`
  query getStudentTableData($params: StudentTableInputInfo) {
    getStudentTableData(params: $params) {
      id
      student_name
      contact_no
      exam_id
      branch_id
      class_id
      section_id
      attended
      branchName
      sectionName
      examName
      className
      marks
      exam_session_id
      accuracy
      exam_rank
      overall_rank
      overall_avg
      overall_avg_include_missed
      profile_pic
      estatus
      subjectMarks {
        subjectId
        subjectName
        subjectMarks
        subjectRank
        subjectTotalQuestions
        subjectRight
        subjectWrong
      }
    }
  }
`;

const EXAM_TYPE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "Full Exam", label: "Full Exams" },
    { value: "Quiz Exam", label: "Quiz Exams" },
];

const SORT_OPTIONS = [
    { value: "nearest", label: "Nearest First" },
    { value: "latest", label: "Latest First" },
    { value: "az", label: "Alphabetical A-Z" },
    { value: "za", label: "Alphabetical Z-A" },
];

// How many of the nearest upcoming exams show on the compact dashboard card
// (the "View More" modal below still lists every authorized exam).
const CARD_EXAM_COUNT = 5;

const widgetCss = `
@keyframes upcomingExamFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.upcoming-exam-item { animation: upcomingExamFadeIn 0.3s ease both; transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease; }
.upcoming-exam-item:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.08); transform: translateY(-2px); border-color: #dbeafe !important; }
@keyframes upcomingExamCountPop { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.upcoming-exam-count-chip { animation: upcomingExamCountPop 0.25s ease both; }
.uex-tab-btn {
    flex: 1;
    padding: 7px 0;
    border: none;
    background: transparent;
    font-size: 12.5px;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
    border-bottom: 2.5px solid transparent;
    transition: color 0.15s ease, border-color 0.15s ease;
    outline: none;
    white-space: nowrap;
}
.uex-tab-btn.active { color: #2563eb; border-bottom-color: #2563eb; }
.uex-tab-btn.active-orange { color: #c2410c; border-bottom-color: #c2410c; }
`;

// The API returns no lifecycle status field (only start_time, no end/duration), so
// Draft/Preparing QS/true "Running" windows aren't derivable — an exam is treated as
// Running for the remainder of its start day, Scheduled before that, Completed after.
const STATUS_STYLES = {
    Scheduled: { background: "#dbeafe", color: "#1d4ed8" },
    Running: { background: "#dcfce7", color: "#15803d" },
    Completed: { background: "#f3f4f6", color: "#6b7280" },
};

// start_time has been observed as either a unix-second string or a plain
// "YYYY-MM-DD HH:mm:ss" datetime string depending on backend version — treat a
// purely numeric value as unix seconds, otherwise let moment parse it directly.
// Getting this wrong silently produces an Invalid Date, which compares as
// neither before nor after "now" and made every exam read as "Completed".
const parseExamStart = (startTime) => {
    const raw = String(startTime == null ? "" : startTime).trim();
    if (raw !== "" && /^-?\d+$/.test(raw)) {
        return moment.unix(Number(raw));
    }
    return moment(raw);
};

const computeExamStatus = (startTime) => {
    const start = parseExamStart(startTime);
    const now = moment();
    if (!start.isValid()) return "Completed";
    if (start.isAfter(now)) return "Scheduled";
    if (start.isSame(now, "day")) return "Running";
    return "Completed";
};

const mergeExams = (data) => {
    if (!data) return [];
    const full = (data.full_exams || []).map((e) => ({
        key: `full-${e.id}`,
        id: `full-${e.id}`,
        examId: e.id,
        exam_name: e.exam_name,
        exam_type: e.exam_type,
        type: "Full Exam",
        class_id: e.class_id,
        start_time: e.start_time,
        sectionNames: (e.section || []).map((s) => s.section_name).filter(Boolean),
    }));
    const quiz = (data.quiz_exams || []).map((e) => ({
        key: `quiz-${e.id}`,
        id: `quiz-${e.id}`,
        examId: e.id,
        exam_name: e.exam_name,
        exam_type: e.exam_type,
        type: "Quiz Exam",
        class_id: e.class_id,
        start_time: e.start_time,
        sectionNames: [],
    }));
    return [...full, ...quiz];
};

const matchesDateRange = (startTime, rangeStart, rangeEnd) => {
    if (!rangeStart || !rangeEnd) return true;
    const start = parseExamStart(startTime);
    if (!start.isValid()) return false;
    return start.isSameOrAfter(moment(rangeStart).startOf("day")) && start.isSameOrBefore(moment(rangeEnd).endOf("day"));
};

const sortExams = (list, sortBy) => {
    const sorted = [...list];
    switch (sortBy) {
        case "latest":
            return sorted.sort((a, b) => parseExamStart(b.start_time).valueOf() - parseExamStart(a.start_time).valueOf());
        case "az":
            return sorted.sort((a, b) => (a.exam_name || "").localeCompare(b.exam_name || ""));
        case "za":
            return sorted.sort((a, b) => (b.exam_name || "").localeCompare(a.exam_name || ""));
        case "nearest":
        default:
            return sorted.sort((a, b) => parseExamStart(a.start_time).valueOf() - parseExamStart(b.start_time).valueOf());
    }
};

const formatSectionsDisplay = (sectionLabels) => {
    if (!sectionLabels || sectionLabels.length === 0) return "-";
    if (sectionLabels.length <= 3) return sectionLabels.join(", ");
    return `${sectionLabels.slice(0, 3).join(", ")} +${sectionLabels.length - 3}`;
};

const statusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.Scheduled;
    return (
        <span
            style={{
                background: style.background,
                color: style.color,
                fontWeight: 700,
                fontSize: 12,
                padding: "3px 12px",
                borderRadius: 20,
                display: "inline-block",
                whiteSpace: "nowrap",
            }}
        >
            {status}
        </span>
    );
};

function UpcomingExamsWidget({ client, branchId, classOptions, allSectionOptions, classMap, selectedExamId, onExamSelect }) {
    const [examsData, setExamsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Full-screen exam analytics — clicking a row in the exams table selects an exam
    // (hiding the table) and fetches getStudentTableData for it, cached by exam id so
    // reopening the same exam never refetches.
    const [selectedExam, setSelectedExam] = useState(null);
    const [studentsData, setStudentsData] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsError, setStudentsError] = useState(false);
    const studentsCache = useRef({});

    const [classFilter, setClassFilter] = useState("");
    const [sectionFilter, setSectionFilter] = useState([]);
    const [examTypeFilter, setExamTypeFilter] = useState("");
    const [dateRangeStart, setDateRangeStart] = useState(null);
    const [dateRangeEnd, setDateRangeEnd] = useState(null);
    const [sortBy, setSortBy] = useState("nearest");

    // Fetch once, whenever the lecturer's branch is known — every filter/sort change
    // below operates purely on this already-fetched response, no refetch. The table's
    // own search box (CardLessDataTableWithSearch) also runs entirely client-side.
    useEffect(() => {
        if (!client || !branchId) return;
        let cancelled = false;
        setLoading(true);
        setError(false);
        client
            .query({
                query: GET_DASHBOARD_EXAMS,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    branch_id: String(branchId),
                },
                fetchPolicy: "network-only",
            })
            .then((result) => {
                if (cancelled) return;
                setExamsData(result.data.getDashBoardExams || null);
                setLoading(false);
            })
            .catch((err) => {
                console.error("UpcomingExamsWidget getDashBoardExams error", err);
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [client, branchId]);

    const mergedExams = useMemo(() => mergeExams(examsData), [examsData]);

    // class_id is the primary authorization boundary — classOptions is already scoped
    // to this lecturer's assigned classes by the parent dashboard.
    const authorizedClassIds = useMemo(() => (classOptions || []).map((c) => String(c.value)), [classOptions]);

    // Section names (not ids — matching on the human-readable name sidesteps id-domain
    // mismatches between this query's section records and GET_BRANCHES's) that this
    // lecturer is actually assigned to, grouped by class_id.
    const authorizedSectionNamesByClass = useMemo(() => {
        const map = {};
        (allSectionOptions || []).forEach((s) => {
            const key = String(s.class_id);
            if (!map[key]) map[key] = new Set();
            map[key].add(String(s.label || "").trim().toLowerCase());
        });
        return map;
    }, [allSectionOptions]);

    const examsWithMeta = useMemo(() => {
        return mergedExams
            .filter((e) => authorizedClassIds.length && authorizedClassIds.includes(String(e.class_id)))
            // Full exams carry their own section list — restrict to sections this
            // lecturer is actually assigned to (not just the class). Quiz exams and
            // full exams with no section data carry no section-level info from the
            // API, so the class_id check above is the only boundary available for them.
            .filter((e) => {
                if (!e.sectionNames.length) return true;
                const allowed = authorizedSectionNamesByClass[String(e.class_id)];
                if (!allowed || !allowed.size) return true;
                return e.sectionNames.some((name) => allowed.has(String(name).trim().toLowerCase()));
            })
            // Synced with the "Select Exam" dropdown in the dashboard header — when a
            // specific exam is chosen there, only that exam_type's records show here.
            .filter((e) => !selectedExamId || parseInt(e.exam_type || "") === parseInt(selectedExamId))
            .map((e) => {
                const className = classMap[e.class_id] || e.class_id;
                return {
                    ...e,
                    class_name: className,
                    status: computeExamStatus(e.start_time),
                    sectionLabels: e.sectionNames.map((name) => `${className}-${name}`),
                    exam_type_display: e.exam_type || e.type,
                };
            });
    }, [mergedExams, authorizedClassIds, authorizedSectionNamesByClass, classMap, selectedExamId]);

    // Nearest exams still to come; when every assigned exam has already happened,
    // fall back to the most recently completed ones so the card is never empty
    // purely because nothing is left on the calendar.
    const cardExams = useMemo(() => {
        const now = moment();
        const upcoming = examsWithMeta
            .filter((e) => {
                const start = parseExamStart(e.start_time);
                return start.isValid() && start.isSameOrAfter(now);
            })
            .sort((a, b) => parseExamStart(a.start_time).valueOf() - parseExamStart(b.start_time).valueOf());
        if (upcoming.length > 0) return upcoming.slice(0, CARD_EXAM_COUNT);
        return [...examsWithMeta]
            .sort((a, b) => parseExamStart(b.start_time).valueOf() - parseExamStart(a.start_time).valueOf())
            .slice(0, CARD_EXAM_COUNT);
    }, [examsWithMeta]);

    const showingRecentFallback = cardExams.length > 0 && cardExams.every((e) => e.status === "Completed");

    // Separate lists for the two tabs ──────────────────────────────────────────
    const upcomingList = useMemo(() =>
        [...examsWithMeta]
            .filter((e) => e.status === "Scheduled" || e.status === "Running")
            .sort((a, b) => parseExamStart(a.start_time).valueOf() - parseExamStart(b.start_time).valueOf())
            .slice(0, CARD_EXAM_COUNT),
        [examsWithMeta]
    );

    const completedList = useMemo(() =>
        [...examsWithMeta]
            .filter((e) => e.status === "Completed")
            .sort((a, b) => parseExamStart(b.start_time).valueOf() - parseExamStart(a.start_time).valueOf())
            .slice(0, CARD_EXAM_COUNT),
        [examsWithMeta]
    );

    const statusCounts = useMemo(() => {
        return examsWithMeta.reduce(
            (acc, e) => {
                acc[e.status] = (acc[e.status] || 0) + 1;
                return acc;
            },
            { Scheduled: 0, Running: 0, Completed: 0 }
        );
    }, [examsWithMeta]);

    const sectionFilterOptions = useMemo(() => {
        const scoped = classFilter
            ? (allSectionOptions || []).filter((s) => String(s.class_id) === String(classFilter))
            : allSectionOptions || [];
        const seen = new Set();
        const opts = [];
        scoped.forEach((s) => {
            const label = `${classMap[s.class_id] || ""}-${s.label}`;
            if (!seen.has(label)) {
                seen.add(label);
                opts.push({ value: label, label });
            }
        });
        return opts;
    }, [allSectionOptions, classFilter, classMap]);

    const filteredExams = useMemo(() => {
        const list = examsWithMeta.filter((e) => {
            if (classFilter && String(e.class_id) !== String(classFilter)) return false;
            if (examTypeFilter && e.type !== examTypeFilter) return false;
            if (sectionFilter.length > 0 && !e.sectionLabels.some((l) => sectionFilter.includes(l))) return false;
            if (!matchesDateRange(e.start_time, dateRangeStart, dateRangeEnd)) return false;
            return true;
        });
        return sortExams(list, sortBy);
    }, [examsWithMeta, classFilter, examTypeFilter, sectionFilter, dateRangeStart, dateRangeEnd, sortBy]);

    const handleClassFilterChange = useCallback((name, value) => {
        setClassFilter(value || "");
        setSectionFilter([]);
    }, []);

    const handleSectionFilterChange = useCallback((name, values) => {
        setSectionFilter(values || []);
    }, []);

    const handleExamTypeChange = useCallback((name, value) => {
        setExamTypeFilter(value || "");
    }, []);

    const handleDateRangeChange = useCallback((startDate, endDate) => {
        setDateRangeStart(startDate);
        setDateRangeEnd(endDate);
    }, []);

    const handleSortChange = useCallback((name, value) => {
        setSortBy(value || "nearest");
    }, []);

    const openModal = useCallback(() => setShowModal(true), []);
    const closeModal = useCallback(() => {
        setShowModal(false);
        setSelectedExam(null);
    }, []);

    const sectionMap = useMemo(() => {
        const map = {};
        (allSectionOptions || []).forEach((s) => {
            map[s.value] = s.label;
        });
        return map;
    }, [allSectionOptions]);

    // Empty cookie means "unrestricted" per authHelper's convention — every subject
    // shows in that case, same as every other authorization check in this dashboard.
    const authorizedSubjectIds = useMemo(() => getAuthorizedSubjectIds(), []);

    const fetchStudentData = useCallback(
        (examRow) => {
            setSelectedExam(examRow);
            const cached = studentsCache.current[examRow.examId];
            if (cached) {
                setStudentsData(cached);
                setStudentsError(false);
                setStudentsLoading(false);
                return;
            }
            setStudentsData([]);
            setStudentsLoading(true);
            setStudentsError(false);
            client
                .query({
                    query: GET_STUDENT_TABLE_DATA,
                    variables: {
                        params: {
                            // institution_id: parseInt(Cookies.get("institutionid")),
                            // branch_id: parseInt(branchId),
                            // exam_id: examRow.examId,
                            // class_id: (classOptions || []).map((c) => c.value).join(","),
                            // section_id: (allSectionOptions || []).map((s) => s.value).join(","),

                            exam_paper_id: +examRow.examId,
                            username: "vignanclg" || "",
                            section_id: (allSectionOptions || []).map((s) => s.value).join(","),
                            exclude_missed: true,


                        },
                    },
                    fetchPolicy: "network-only",
                })
                .then((result) => {
                    const rows = (result.data && result.data.getStudentTableData) || [];
                    studentsCache.current[examRow.examId] = rows;
                    setStudentsData(rows);
                    setStudentsLoading(false);
                })
                .catch((err) => {
                    console.error("UpcomingExamsWidget getStudentTableData error", err);
                    setStudentsError(true);
                    setStudentsLoading(false);
                });
        },
        [client, branchId, classOptions, allSectionOptions]
    );

    const handleBackToList = useCallback(() => setSelectedExam(null), []);
    const handleCloseAnalytics = useCallback(() => {
        setSelectedExam(null);
        setShowModal(false);
    }, []);

    // Exam names (card lists + the View All table) open the parent dashboard's shared
    // Exam Details modal — same target as the Exam Reports table's name column. That
    // modal reads a getExamReports-shaped row, so map this widget's row onto it;
    // totalStudents isn't available from getDashBoardExams and is left off (the modal
    // falls back to the detail response's own appeared count).
    // Without the callback, fall back to this widget's own analytics overlay.
    const openExamDetails = useCallback(
        (examRow) => {
            if (!onExamSelect) {
                fetchStudentData(examRow);
                return;
            }
            // The details modal is a plain fixed overlay, which react-bootstrap's Modal
            // would otherwise stack on top of — close the View All modal first.
            setShowModal(false);
            onExamSelect({
                id: examRow.examId,
                name: examRow.exam_name,
                className: examRow.class_name,
            });
        },
        [onExamSelect, fetchStudentData]
    );

    const examTableColumns = useMemo(
        () => [
            {
                dataField: "exam_name",
                text: "Exam Name",
                sort: true,
                formatter: (cell, row) => (
                    <div
                        role="button"
                        onClick={() => openExamDetails(row)}
                        style={{ color: "#2563eb", fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
                        title="View exam details"
                    >
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fal fa-file-alt" style={{ fontSize: 12 }} />
                        </div>
                        <span style={{ textDecoration: "underline", textUnderlineOffset: 2 }}>{cell}</span>
                    </div>
                ),
            },
            {
                dataField: "start_time",
                text: "Date & Time",
                sort: true,
                formatter: (cell) => {
                    const start = parseExamStart(cell);
                    return start.isValid() ? start.format("DD MMM YYYY, hh:mm A") : "-";
                },
            },
            { dataField: "class_name", text: "Class", sort: true },
            {
                dataField: "sectionLabels",
                text: "Sections",
                formatter: (cell) => formatSectionsDisplay(cell),
            },
            { dataField: "exam_type_display", text: "Exam Type", sort: true },
            { dataField: "status", text: "Status", sort: true, formatter: (cell) => statusBadge(cell) },
            {
                dataField: "key",
                text: "",
                formatter: (cell, row) => (
                    <button
                        type="button"
                        onClick={() => fetchStudentData(row)}
                        title="View exam analytics"
                        aria-label="View exam analytics"
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            border: "1px solid #bfdbfe",
                            background: "#eff6ff",
                            color: "#2563eb",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                    >
                        <i className="fal fa-arrow-right" style={{ fontSize: 13 }} />
                    </button>
                ),
            },
        ],
        [fetchStudentData, openExamDetails]
    );

    const selectedSectionOptions = sectionFilterOptions.filter((o) => sectionFilter.includes(o.value));

    return (
        <>
            <style>{widgetCss}</style>
            <Card className="modern-card border-0 shadow-sm h-100">
                <Card.Body>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <div
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 8,
                                    background: "#eff6ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <i className="fal fa-calendar-alt" style={{ color: "#2563eb", fontSize: 16 }} />
                            </div>
                            <h5 className="font-weight-bold text-dark mb-0">Exams &amp; History</h5>
                        </div>
                        {!loading && !error && (
                            <span
                                className="upcoming-exam-count-chip"
                                style={{
                                    background: "#dbeafe",
                                    color: "#1d4ed8",
                                    fontWeight: 700,
                                    fontSize: 12,
                                    padding: "4px 12px",
                                    borderRadius: 20,
                                }}
                            >
                                {examsWithMeta.length} Total
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className="lecture-exam-shim w-100" style={{ height: 160 }} />
                    ) : error ? (
                        <p className="text-danger mb-0">Unable to load exams.</p>
                    ) : (
                        <>
                            {/* ── Latest Exams (Scheduled / Running) ── */}
                            <p className="mb-1" style={{ fontSize: 12, fontWeight: 700, color: "#c2410c", letterSpacing: "0.02em" }}>
                                <i className="fal fa-calendar-star" style={{ marginRight: 5 }} />
                                Latest Exams
                            </p>
                            {upcomingList.length === 0 ? (
                                <p className="text-muted mb-2" style={{ fontSize: 12 }}>No upcoming exams.</p>
                            ) : (
                                <div style={{ maxHeight: 160, overflowY: "auto", marginBottom: 10 }}>
                                    {upcomingList.map((exam, idx) => (
                                        <div
                                            key={exam.key}
                                            className="upcoming-exam-item"
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: 8,
                                                marginBottom: 5,
                                                border: "1px solid #dbeafe",
                                                background: "#f8fbff",
                                                animationDelay: `${idx * 50}ms`,
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div
                                                        role="button"
                                                        onClick={() => openExamDetails(exam)}
                                                        title="View exam details"
                                                        className="font-weight-bold"
                                                        style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                                                    >
                                                        {exam.exam_name}
                                                    </div>
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: 11, gap: 5, marginTop: 1 }}>
                                                        <i className="fal fa-clock" />
                                                        {parseExamStart(exam.start_time).isValid() ? parseExamStart(exam.start_time).format("DD MMM YYYY, hh:mm A") : "-"}
                                                    </span>
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: 11, gap: 5 }}>
                                                        <i className="fal fa-users" />
                                                        {exam.class_name}{exam.sectionLabels.length > 0 ? ` (${formatSectionsDisplay(exam.sectionLabels)})` : ""}
                                                    </span>
                                                </div>
                                                {statusBadge(exam.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <hr style={{ margin: "8px 0" }} />

                            {/* ── Completed History ── */}
                            <p className="mb-1" style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", letterSpacing: "0.02em" }}>
                                <i className="fal fa-history" style={{ marginRight: 5 }} />
                                Completed History
                            </p>
                            {completedList.length === 0 ? (
                                <p className="text-muted mb-0" style={{ fontSize: 12 }}>No completed exams yet.</p>
                            ) : (
                                <div style={{ maxHeight: 140, overflowY: "auto" }}>
                                    {completedList.map((exam, idx) => (
                                        <div
                                            key={exam.key}
                                            className="upcoming-exam-item"
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: 8,
                                                marginBottom: 5,
                                                border: "1px solid #dcfce7",
                                                background: "#f8fffe",
                                                animationDelay: `${idx * 50}ms`,
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div
                                                        role="button"
                                                        onClick={() => openExamDetails(exam)}
                                                        title="View exam details"
                                                        className="font-weight-bold"
                                                        style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                                                    >
                                                        {exam.exam_name}
                                                    </div>
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: 11, gap: 5, marginTop: 1 }}>
                                                        <i className="fal fa-clock" />
                                                        {parseExamStart(exam.start_time).isValid() ? parseExamStart(exam.start_time).format("DD MMM YYYY, hh:mm A") : "-"}
                                                    </span>
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: 11, gap: 5 }}>
                                                        <i className="fal fa-users" />
                                                        {exam.class_name}{exam.sectionLabels.length > 0 ? ` (${formatSectionsDisplay(exam.sectionLabels)})` : ""}
                                                    </span>
                                                </div>
                                                {statusBadge(exam.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* View All button */}
                    {/* {!loading && !error && examsWithMeta.length > 0 && (
                        <div className="text-right mt-2">
                            <span
                                role="button"
                                onClick={openModal}
                                style={{ color: "#2563eb", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                            >
                                View All &rarr;
                            </span>
                        </div>
                    )} */}
                </Card.Body>
            </Card>

            {/* Hidden (not just covered) while the full-screen analytics overlay is open —
                react-bootstrap's Modal enforces focus back into its own dialog, which
                fights any interactive element (like the analytics filter dropdowns)
                rendered outside it and made those dropdowns unclickable. */}
            <Modal show={showModal && !selectedExam} onHide={closeModal} size="lg" centered scrollable dialogClassName="lecturer-dashboard-modern">
                <Modal.Header closeButton>
                    <Modal.Title>Exams &amp; History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3 mb-3">
                        <Col xs={12} sm={6} md={3}>
                            <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Class</Form.Label>
                            <SelectDropDown
                                name="classFilter"
                                placeholderName="All Classes"
                                options={classOptions}
                                stateData={classFilter ? classOptions.find((c) => String(c.value) === String(classFilter)) || null : null}
                                handleChange={handleClassFilterChange}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                            <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Section</Form.Label>
                            <SelectDropDown
                                name="sectionFilter"
                                placeholderName="All Sections"
                                options={sectionFilterOptions}
                                stateData={selectedSectionOptions}
                                isMulti
                                handleChange={handleSectionFilterChange}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                            <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Exam Type</Form.Label>
                            <SelectDropDown
                                name="examTypeFilter"
                                placeholderName="All"
                                options={EXAM_TYPE_FILTER_OPTIONS.filter((o) => o.value)}
                                stateData={examTypeFilter ? EXAM_TYPE_FILTER_OPTIONS.find((o) => o.value === examTypeFilter) : null}
                                handleChange={handleExamTypeChange}
                            />
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                            <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Date</Form.Label>
                            {/* <SelectDropDown
                                name="sortBy"
                                placeholderName="Sort"
                                options={SORT_OPTIONS}
                                stateData={SORT_OPTIONS.find((o) => o.value === sortBy)}
                                isClearable={false}
                                handleChange={handleSortChange}
                            /> */}
                            <DateBlock applyCallback={handleDateRangeChange} />

                        </Col>
                    </Row>
                    {/* 
                    <Row className="g-3 mb-3">
                        <Col xs={12} sm={6} md={4}>
                            <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Date Range</Form.Label>
                            <div>
                                <DateBlock applyCallback={handleDateRangeChange} />
                            </div>
                        </Col>
                    </Row> */}

                    {loading ? (
                        <div>
                            {[...Array(5)].map((_, idx) => (
                                <div key={idx} className="lecture-exam-shim w-100 mb-2" style={{ height: 36 }} />
                            ))}
                        </div>
                    ) : error ? (
                        <p className="text-danger mb-0">Unable to load exams.</p>
                    ) : filteredExams.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fal fa-calendar-times text-muted" style={{ fontSize: 30 }} />
                            <p className="text-muted mb-0 mt-2">No Exams Found</p>
                        </div>
                    ) : (
                        <CardLessDataTableWithSearch
                            parentData={filteredExams}
                            particlecolumns={examTableColumns}
                            defaultSorted={[{ dataField: "start_time", order: "asc" }]}
                            name="upcoming_exams"
                            placeholder="Search exams..."
                        />
                    )}
                </Modal.Body>
            </Modal>

            {selectedExam && (
                <ExamAnalyticsDashboard
                    exam={selectedExam}
                    students={studentsData}
                    loading={studentsLoading}
                    error={studentsError}
                    classMap={classMap}
                    sectionMap={sectionMap}
                    classOptions={classOptions}
                    allSectionOptions={allSectionOptions}
                    authorizedSubjectIds={authorizedSubjectIds}
                    onBack={handleBackToList}
                    onClose={handleCloseAnalytics}
                    parseExamStart={parseExamStart}
                    formatSectionsDisplay={formatSectionsDisplay}
                />
            )}
        </>
    );
}

export default UpcomingExamsWidget;
